import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUpRight, Sprout } from "lucide-react";
import { useLanguage } from "../i18n";
import { scrollToSection } from "../lib/scroll";
import { MagneticWrap } from "./MagneticWrap";
import { HeroScene3D } from "./HeroScene3D";

const HERO_IMG =
  "https://images.unsplash.com/photo-1512650268540-a25f25363a68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHx2YXN0JTIwZmFybSUyMGxhbmRzY2FwZSUyMHN1bnNldCUyMGRyb25lfGVufDB8fHx8MTc4OTUwNDIxOXww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const routes = [...t.hero.routes, ...t.hero.routes];

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden bg-[#04120b]">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Vast agricultural plains at golden hour" className="h-full w-full object-cover" />
        <div className="absolute inset-0">
          <HeroScene3D />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#04120b]/85 via-[#04120b]/30 to-[#04120b]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(4,18,11,0.5)_100%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-5 pt-40 pb-24 text-center sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-[#48e38e]/30 bg-[#48e38e]/10 px-4 py-2 backdrop-blur-sm"
          data-testid="hero-badge"
        >
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#48e38e]" />
          <span className="font-mono text-[11px] font-medium uppercase tracking-[0.28em] text-[#48e38e]">
            {t.hero.badge}
          </span>
        </motion.div>

        <motion.h1
          style={{ y: titleY }}
          key={lang}
          className="font-display mx-auto max-w-4xl text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.25}>{t.hero.titleLine1}</MaskedLine>
          <MaskedLine delay={0.4} className="text-shimmer">
            {t.hero.titleLine2}
          </MaskedLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
          data-testid="hero-subheadline"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <MagneticWrap>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToSection("#faaliyetler")}
              className="btn-sweep group inline-flex items-center gap-2 rounded-full bg-[#48e38e] px-7 py-3.5 text-sm font-semibold text-[#062316] transition-shadow duration-300 hover:shadow-[0_0_32px_rgba(72,227,142,0.5)]"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </MagneticWrap>
          <MagneticWrap>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToSection("#iletisim")}
              className="btn-liquid group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </MagneticWrap>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-16 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white/40"
          data-testid="hero-scroll-hint"
        >
          <ArrowDown size={14} className="animate-bounce text-[#48e38e]" />
          {t.hero.scroll}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="relative z-10 border-t border-white/10 bg-[#04120b]/60 backdrop-blur-md"
        data-testid="hero-route-ticker"
      >
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-5 py-4 sm:px-8">
          <span className="hidden shrink-0 items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#48e38e] md:flex">
            <Sprout size={13} />
            {t.hero.routesLabel}
          </span>
          <div className="flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
            <div className="marquee-track" style={{ "--marquee-duration": "30s" }}>
              {routes.map((r, i) => (
                <span key={i} className="flex items-center font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                  <span className="whitespace-nowrap px-6">{r}</span>
                  <span className="h-1 w-1 rounded-full bg-[#48e38e]/60" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
